import React, { useCallback, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstTable from "../../components/DstTable/DstTable";
import { useSession } from "../../contexts/SessionContext";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { EphemeralImportFile } from "./EphemeralImport.d";
import { MAX_PROPOSITIONS, parseCsvFile, submitEphemeralExercisesImport } from "./EphemeralImport.function";

export const EphemeralImport = () => {
  const { language, showAlert, popAlert } = useSession();
  const [csvData, setCsvData] = useState<Array<EphemeralImportFile>>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const onFileUpload = useCallback(
    (file: File) => {
      parseCsvFile(file, setCsvData, language);
    },
    [language]
  );

  const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileUpload(file);
    }
  };

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      dropZoneRef.current?.classList.remove("drag-over");
      const file = event.dataTransfer?.files[0];
      if (file) {
        onFileUpload(file);
      }
    },
    [onFileUpload]
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dropZoneRef.current?.classList.add("drag-over");
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dropZoneRef.current?.classList.remove("drag-over");
  }, []);

  const columns = [
    {
      name: translate(language, "PAGES.EPHEMERAL_IMPORT.TABLE.SCENARIO"),
      key: "scenario",
      render: (line: EphemeralImportFile) => <span style={{ whiteSpace: "pre-wrap" }}>{line.scenario}</span>,
    },
    ...Array.from({ length: MAX_PROPOSITIONS }, (_, index) => ({
      name: translate(language, "PAGES.EPHEMERAL_IMPORT.TABLE.PROPOSITION").replace(
        "{{INDEX}}",
        String(index + 1).padStart(2, "0")
      ),
      key: `propositions.${index}`,
      render: (line: EphemeralImportFile) => (
        <span className={line.propositions[index] ? "" : "text-muted"}>{line.propositions[index] || "N/A"}</span>
      ),
    })),
    {
      name: translate(language, "PAGES.EPHEMERAL_IMPORT.TABLE.ANSWER"),
      key: "answer",
    },
  ];

  return (
    <div className="p-3 w-100" id="ephemeral-import">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex">
              <span
                className="dst-icon-chevron-left p-2 medium-gray-background rounded me-2 clickable"
                onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                  completeNavigate(event, navigate, "/ephemeral/sessions")
                }
              />
              <h2 className="mb-0">{translate(language, "PAGES.EPHEMERAL_IMPORT.TITLE")}</h2>
            </div>
            <div className="d-flex gap-2">
              {csvData.length > 0 && (
                <DstButton
                  btnImageBefore={<span className="dst-icon-upload-03 me-2" />}
                  value={translate(language, "PAGES.EPHEMERAL_IMPORT.NEW_CSV_IMPORT")}
                  variant="light"
                  clickFunction={() => {
                    setCsvData([]);
                    const input = document.getElementById("fileInput") as HTMLInputElement | null;
                    if (input) {
                      input.value = "";
                      input.click();
                    }
                  }}
                />
              )}
              <DstButton
                btnImageBefore={<span className="dst-icon-download-02 me-2" />}
                value={translate(language, "PAGES.EPHEMERAL_IMPORT.DOWNLOAD_TEMPLATE")}
                variant="light"
                clickFunction={() => {
                  const link = document.createElement("a");
                  link.href = "/share/ephemeral_exercises_template.csv";
                  link.download = "ephemeral_exercises_template.csv";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              />
              <DstButton
                btnImageAfter={<span className="dst-icon-arrow-right ms-2" />}
                value={translate(language, "PAGES.EPHEMERAL_IMPORT.CREATE_EXERCISES")}
                variant="primary"
                loading={submitting}
                disabled={submitting || csvData.length === 0}
                clickFunction={() => {
                  if (showAlert && popAlert) {
                    submitEphemeralExercisesImport(csvData, language, setSubmitting, showAlert, popAlert, setCsvData);
                  }
                }}
              />
            </div>
          </div>
        </Col>
      </Row>
      {csvData.length === 0 ? (
        <div className="d-flex flex-column gap-2 clickable">
          <DstTable data={csvData} columns={columns} itemsPerPage={20} />
          <div
            ref={dropZoneRef}
            className="border-2 border-dashed rounded-3 p-5 text-center d-flex flex-column align-items-center"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={onDrop}
          >
            <div className="text-center s-regular d-flex align-items-center">
              {translate(language, "PAGES.EPHEMERAL_IMPORT.DROP_FILE")}
              <DstButton
                value={translate(language, "PAGES.EPHEMERAL_IMPORT.LOAD_LOCAL_FILE")}
                variant="link"
                btnClass="ps-1"
                clickFunction={() => document.getElementById("fileInput")?.click()}
              />
            </div>
          </div>
        </div>
      ) : (
        <DstTable
          data={csvData.map((item, index) => ({ ...item, _index: index }))}
          columns={columns}
          itemsPerPage={20}
          classTable="xs-bold vertical-align-middle"
        />
      )}
      <input type="file" id="fileInput" className="d-none" accept=".csv" onChange={onFileInputChange} />
    </div>
  );
};

export default EphemeralImport;
